import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, Instagram, Upload, Image, Truck, Type, FolderTree, Sparkles, LogOut, Loader2, Plus, Trash2, Edit3, Check, X, Tag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import AdminSidebar, { AdminLayout } from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEventTypes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    fetchEventTypes(token);
  }, [navigate]);

  const fetchEventTypes = async (token) => {
    try {
      if (!token) token = localStorage.getItem("adminToken");
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      const res = await axios.get(`${API}/settings/event-types?active_only=false`);
      setEventTypes(res.data.event_types || []);
    } catch {
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const saveEventTypes = async (updated) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/settings/event-types`, { event_types: updated }, { headers: { Authorization: `Bearer ${token}` } });
      setEventTypes(updated);
      toast.success("Event types saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    const id = newName.trim().toLowerCase().replace(/\s+/g, "-");
    if (eventTypes.some(et => et.id === id)) { toast.error("Already exists"); return; }
    const updated = [...eventTypes, { id, name: newName.trim(), active: true }];
    saveEventTypes(updated);
    setNewName("");
  };

  const handleToggleActive = (id) => {
    const updated = eventTypes.map(et => et.id === id ? { ...et, active: !et.active } : et);
    saveEventTypes(updated);
  };

  const handleRemove = (id) => {
    // Soft delete — mark inactive, don't remove from products
    const updated = eventTypes.map(et => et.id === id ? { ...et, active: false, deleted: true } : et);
    saveEventTypes(updated);
    toast.success("Event type removed (soft delete). It will not appear in filters but remains on existing products.");
  };

  const handleStartEdit = (et) => { setEditingId(et.id); setEditName(et.name); };

  const handleSaveEdit = () => {
    if (!editName.trim()) return;
    const updated = eventTypes.map(et => et.id === editingId ? { ...et, name: editName.trim() } : et);
    saveEventTypes(updated);
    setEditingId(null);
  };

  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  if (loading) return <AdminLayout><AdminLoadingState message="Loading event types..." /></AdminLayout>;

  const activeTypes = (eventTypes || []).filter(et => !et.deleted);
  const deletedTypes = (eventTypes || []).filter(et => et.deleted);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />

      <main className="lg:ml-64 p-8">
        <div className="max-w-2xl">
          <h1 className="text-2xl text-[#282828] font-light mb-2" data-testid="admin-event-types-title">Event Types</h1>
          <p className="text-[13px] text-[#676664]/60 mb-8">Manage event types for product categorisation and catalogue filtering. Removing an event type hides it from filters but does not remove it from existing products.</p>

          {/* Add New */}
          <div className="flex items-center gap-3 mb-8" data-testid="add-event-type-form">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New event type name..."
              className="max-w-xs border-[#282828]/10 text-sm"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              data-testid="new-event-type-input"
            />
            <Button onClick={handleAdd} disabled={!newName.trim() || saving} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[11px] tracking-[0.1em] uppercase" data-testid="add-event-type-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
            </Button>
          </div>

          {/* Active Event Types */}
          <div className="space-y-2" data-testid="event-types-list">
            {activeTypes.map(et => (
              <div key={et.id} className="flex items-center justify-between px-4 py-3 bg-white border border-[#282828]/5" data-testid={`event-type-row-${et.id}`}>
                <div className="flex items-center gap-3">
                  {/* Active toggle */}
                  <button
                    onClick={() => handleToggleActive(et.id)}
                    className={`w-10 h-5 rounded-full relative transition-colors ${et.active ? 'bg-[#4A7C59]' : 'bg-[#282828]/15'}`}
                    data-testid={`toggle-${et.id}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${et.active ? 'left-5' : 'left-0.5'}`} />
                  </button>

                  {editingId === et.id ? (
                    <div className="flex items-center gap-2">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 w-48 text-sm border-[#9A7B4F]" autoFocus onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()} data-testid={`edit-input-${et.id}`} />
                      <button onClick={handleSaveEdit} className="text-[#4A7C59] hover:text-[#3d6b4a]"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingId(null)} className="text-[#676664]/40 hover:text-[#676664]"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <span className={`text-sm ${et.active ? 'text-[#282828]' : 'text-[#676664]/40 line-through'}`}>{et.name}</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button onClick={() => handleStartEdit(et)} className="text-[#676664]/30 hover:text-[#9A7B4F] transition-colors" data-testid={`edit-${et.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleRemove(et.id)} className="text-[#676664]/30 hover:text-red-400 transition-colors" data-testid={`remove-${et.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Deleted types info */}
          {deletedTypes.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[#282828]/5">
              <p className="text-[11px] text-[#676664]/40 mb-2 uppercase tracking-wider">Removed ({deletedTypes.length})</p>
              <div className="flex flex-wrap gap-2">
                {deletedTypes.map(et => (
                  <span key={et.id} className="text-[11px] text-[#676664]/30 line-through px-2 py-0.5 bg-[#f0f0f0]">{et.name}</span>
                ))}
              </div>
            </div>
          )}

          {saving && <div className="flex items-center gap-2 mt-4"><Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" /><span className="text-[12px] text-[#676664]/50">Saving...</span></div>}
        </div>
      </main>
    </div>
  );
}
